import cookie from 'cookie';
import queryString from 'query-string';
import mapKeyValues, { transforms as t } from 'lib/mapKeyValues';

/**
 * Standardizes the site value to the base site value (example: hgtv-photos -> hgtv)
 * @param  {string} siteValue Unmodified site value
 * @return {string}           Modified site value
 */
export function normalizeSiteValue(siteValue) {
  const siteValueMap = [
    { test: /^(golden-)?hgtv([-_]\w+)?$/i, value: 'hgtv' },
    { test: /^(golden-)?cook([-_]\w+)?$/i, value: 'cook' },
    { test: /^(golden-)?gac([-_]\w+)?$/i, value: 'gac' },
    { test: /^(golden-)?diy([-_]\w+)?$/i, value: 'diy' },
    { test: /^(golden-)?food([-_]\w+)?$/i, value: 'food' },
    { test: /^(golden-)?travel([-_]\w+)?$/i, value: 'travel' },
    { test: /^(golden-)?tlc([-_]\w+)?$/i, value: 'tlc' },
    { test: /^(golden-)?discovery([-_]\w+)?$/i, value: 'dsc/discovery' },
    { test: /^(golden-)?investigationdiscovery([-_]\w+)?$/i, value: 'investigationdiscovery' },
  ];

  const match = siteValueMap.find(item => item.test instanceof RegExp && item.test.exec(siteValue));
  return match ? match.value : siteValue;
}

const isSweepsPage = (classification) =>
  String(classification).split(',').indexOf('sweepstakes') >= 0;

const normalizePageType = (pagetype, otherMeta) => {
  if (!pagetype && otherMeta.type) return otherMeta.type;
  return pagetype;
};

/**
 * List of keys that can be carried over from mdManager
 * @type {Array}
 */
export const validKeys = [
  { key: 'site', transform: [normalizeSiteValue] },
  'type',
  { key: 'pagetype', transform: [normalizePageType] },
  'keyterm',
  'adkey1',
  'talentName',
  'showName',
  'keyword',
  'contenttag1',
  'contenttag2',
  'categorydspname',
  { key: 'categorydspname', mappedKey: 'category' },
  'photocount',
  'vgncontent',
  'show_abbr',
  'sniads_regression_test',
  'ing',
  'scripps160',
  'scripps250',
  'scripps600',
  'scripps728',
  'scripps320',
  'scripps250btf',
  'scripps250m',
  'cs_debug',
  'sponsorship',
  { key: 'sponsorship', mappedKey: 'topic' },
  'subsection',
  'subsection1',
  'subsection2',
  'subsctn1',
  'subsctn2',
  'source',
  'livingsource',
  'orntn',
  'version',
  'sbi_apoc',
  'sbi_size',
  'sbi_dozer',
  'glist',
  'mcid',
  'aam_fw',
  'classification',
  {
    key: 'classification',
    mappedKey: 'classification_list',
    transform: [t.split(','), t.mapTransform(t.toLowerCase)],
  },
  'strnativekey',
  'abkey',
  'kuid',
  'ksg',
  'light_mode',
  'delivery_channel',
  { key: 'delivery_channel', mappedKey: 'layout' },
  { key: 'sctndspname', transform: [t.toLowerCase] },
  'targetedterms',
  'uniqueid',
  {
    key: 'uniqueid',
    mappedKey: 'page_uniqueid',
    transform: [t.split('|'), t.mapTransform(t.toLowerCase)],
  },
  { key: 'classification', mappedKey: 'is_sweeps_page', transform: [isSweepsPage] },
  // Currently takes too long for this value to be added to be useful
  // { key: 'sweepsid', mappedKey: 'is_sweeps_entry', transform: [truthy] },
];

export const validKeysForFood = [
  {
    key: 'mainingredient',
    mappedKey: 'main_ing',
    transform: [t.split(';'), t.mapTransform(t.sanitizeValueIncDash)],
  },
  {
    key: 'targetedterms',
    mappedKey: 'ing',
    transform: [t.split(';'), t.mapTransform(t.sanitizeValueIncDash)],
  },
];

export function getCookieValues() {
  const cookies = cookie.parse(window.document.cookie);
  const validCookies = {};

  if (cookies.aam_fw) {
    validCookies.aam_fw = cookies.aam_fw.split(';').map(i => i.split('=')[1]);
  }

  validCookies.us_privacy = cookies.us_privacy;

  if (cookies['AMCV_BC501253513148ED0A490D45%40AdobeOrg']) {
    const adobeCookie = ['id', ...cookies['AMCV_BC501253513148ED0A490D45%40AdobeOrg'].split('|')]
      .reduce((prev, current, index, array) =>
        (index % 2 === 0 ? { ...prev, [current]: array[index + 1] } : prev),
      {});

    validCookies.mcid = adobeCookie.MCMID;
  }

  return validCookies;
}

/**
 * Retrieves meta data from mdManager and returns it
 * @return {Object} Meta data from mdManager
 */
export default function standardSiteMetaData() {
  try {
    const { mdManager } = window;
    const { hostname, search } = window.location;
    /* eslint-disable-next-line camelcase */
    const { ad_topic, source } = queryString.parse(search);

    const getValueFromMdManager = key => mdManager.getParameterString(key);

    let keyValues = mapKeyValues(validKeys, getValueFromMdManager);

    const defaultTransform = [t.sanitizeValueIncDash];

    if (keyValues.site === 'food') {
      keyValues = {
        ...keyValues,
        ...mapKeyValues(validKeysForFood, getValueFromMdManager, defaultTransform),
      };
    }

    const cookieValues = getCookieValues();

    // Add querystring parameters to page meta, if they exist
    /* eslint-disable camelcase */
    if (ad_topic !== undefined) {
      keyValues.topic = ad_topic;
    }
    /* eslint-enable camelcase */

    if (source !== undefined) {
      keyValues.source = source;
    }

    // Workaround for identifying author and golden sites, so we can serve test ads to them
    if (/^author\./i.test(hostname)) keyValues.environment = 'author';
    if (/^golden-www\./i.test(hostname)) keyValues.environment = 'golden';

    // Looks for the section that contains the SWEEPS element
    keyValues.is_sweeps_entry = !!document.querySelector('div.engagesciences');

    keyValues.config_channel = 'standard';

    return { ...keyValues, ...cookieValues };
  } catch (e) {
    throw new Error(e);
  }
}
